import dayjs, { Dayjs } from "dayjs";

// Tipos minimos necesarios por estas helpers, redeclarados aqui para evitar
// circular import con calendarCreateModal.tsx (que reexporta los tipos
// canonicos para consumidores externos).
export type Third = "morning" | "mid" | "evening";
export type MoneyLine = { name: string; _f_val: number };
export type MoneyLines = MoneyLine[];

// Tercios canónicos (mañana / mid / evening). Ver
// docs/calendario-logica.md §2.1 para la matemática de tercios y
// §1.5 (glosario humano). El rango mostrable usa intervalos cerrados en el
// último minuto ocupado: `00:00–10:59`, `11:00–14:59`, `15:00–23:59`.
export const THIRD_LABEL: Record<Third, string> = {
  morning: "Mañana (00:00–10:59)",
  mid: "Medio día (11:00–14:59)",
  evening: "Tarde/Noche (15:00–23:59)",
};

export const THIRD_ORDER: Third[] = ["morning", "mid", "evening"];

export function daysBetween(startISO: string, endISO: string) {
  // start/end: YYYY-MM-DD
  const s = new Date(`${startISO}T00:00:00`);
  const e = new Date(`${endISO}T00:00:00`);
  const ms = e.getTime() - s.getTime();
  return Math.max(0, Math.round(ms / (1000 * 60 * 60 * 24)));
}

export function n(value: any) {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

export function roundPeso(x: number) {
  // CLP sin decimales
  return Math.round(x);
}

export function cmpISODate(a: string, b: string) {
  // YYYY-MM-DD lex
  if (a === b) return 0;
  return a < b ? -1 : 1;
}

export function thirdIdx(t: Third) {
  return THIRD_ORDER.indexOf(t);
}

export function thirdToTimeStart(t: Third) {
  if (t === "morning") return "00:00:00";
  if (t === "mid") return "11:00:00";
  return "15:00:00";
}

export function thirdToTimeEnd(t: Third) {
  if (t === "morning") return "10:59:59";
  if (t === "mid") return "14:59:59";
  return "23:59:59";
}

export function hhmmToMin(hhmm: string) {
  const [h, m] = hhmm.split(":").map((x) => Number(x));
  return (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0);
}

export function minToThird(min: number): Third {
  // morning: 00:00–10:59, mid: 11:00–14:59, evening: 15:00–23:59
  if (min < 11 * 60) return "morning";
  if (min < 15 * 60) return "mid";
  return "evening";
}

export function dayjsOrDefaultTime(hhmm: string): Dayjs {
  return dayjs(`2000-01-01 ${hhmm}`, "YYYY-MM-DD HH:mm");
}

export function toIsoWithSeconds(
  dateISO: string,
  hhmm: string,
  isEnd: boolean,
) {
  const secs = isEnd ? "59" : "00";
  return `${dateISO}T${hhmm}:${secs}`;
}

export function normalizeMoneyLines(x: any): MoneyLines {
  if (!Array.isArray(x)) return [];
  return x
    .map((it) => ({
      name: String(it?.name ?? "").trim(),
      _f_val: Number(it?._f_val ?? 0),
    }))
    .filter((it) => it.name.length > 0 && Number.isFinite(it._f_val));
}

export function sumMoneyLines(lines: MoneyLines | undefined) {
  return (lines ?? []).reduce(
    (acc, it) => acc + (Number(it?._f_val) || 0),
    0,
  );
}

/**
 * Builder de `disabledTime` para AntD TimePicker. Restringe horas y
 * minutos seleccionables al rango del tercio dado:
 *   - morning: 00:00-10:59
 *   - mid: 11:00-14:59
 *   - evening: 15:00-23:59
 *
 * Devuelve una funcion thunk para encajar con la API del TimePicker
 * de AntD (que espera `disabledTime: () => ({ disabledHours, disabledMinutes })`).
 */
export const buildThirdDisabledTime = (third: Third) => () => {
  const ranges = {
    morning: { min: 0, max: 10 * 60 + 59 },
    mid: { min: 11 * 60, max: 14 * 60 + 59 },
    evening: { min: 15 * 60, max: 23 * 60 + 59 },
  };
  const { min, max } = ranges[third];
  return {
    disabledHours: () =>
      Array.from({ length: 24 }, (_, h) => {
        const hourStart = h * 60;
        const hourEnd = h * 60 + 59;
        return hourEnd < min || hourStart > max ? h : null;
      }).filter((h): h is number => h !== null),
    disabledMinutes: (selectedHour: number | null) => {
      if (selectedHour == null) return [];
      const hourStart = selectedHour * 60;
      return Array.from({ length: 60 }, (_, m) => m).filter((m) => {
        const total = hourStart + m;
        return total < min || total > max;
      });
    },
  };
};
